import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
} from "@components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ChevronDownIcon,
  CopyIcon,
  MinusIcon,
  PlusIcon,
  Settings2Icon,
  Trash2Icon,
} from "lucide-react";
import {
  Controller,
  UseFieldArrayReturn,
  UseFormReturn,
  useWatch,
} from "react-hook-form";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { i18n } from "@lingui/core";
import {
  Language,
  Source,
  LANGUAGE_CODE_TO_LABEL,
  SOURCE_CODE_TO_LABEL,
  MentionSourceType,
  MENTION_SOURCE_TYPES,
  MENTION_SOURCE_TYPE_TO_LABEL,
  SOURCE,
  MENTION_SOURCE_TYPE,
} from "@/constants";
import { QueryBuilderForm } from "../../types";
import {
  getMinReachByChannelTypeDefaultValues,
  MinReachByChannelTypeSource,
  MinReachByChannelTypeValues,
  QueryIn,
} from "@/containers/QueryBuilderNew/requestUtils";
import BadgeInput from "@components/ui/badge-input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Switch } from "@components/ui/switch";
import { Tag } from "@/types/Tag";
import TagSelector from "./partials/TagSelector";
import { useState } from "react";
import { useOrganization } from "@/hooks/useOrganization";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { plural } from "@lingui/core/macro";
import { Input } from "@components/ui/input";
import { PopoverClose } from "@radix-ui/react-popover";

const DISTANCE_VALUES = [1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 40, 50];

type QueryProps = {
  queriesField: UseFieldArrayReturn<QueryBuilderForm, "queries">;
  minReachByChannelTypeSources: MinReachByChannelTypeSource[];
  form: UseFormReturn<QueryBuilderForm>;
  socialSources: Source[];
  audioSources: Source[];
  languages: Language[];
  defaultQuery: QueryIn;
  newsSources: Source[];
  queries: QueryIn[];
  sources: Source[];
  index: number;
  tags: Tag[];
};

export default function Query({
  minReachByChannelTypeSources,
  index: queryIndex,
  socialSources,
  audioSources,
  queriesField,
  defaultQuery,
  newsSources,
  languages,
  sources,
  queries,
  tags,
  form,
}: QueryProps) {
  const { t } = useLingui();

  // Server state
  const { data: orgData } = useOrganization();
  const { plan: { max_num_queries_per_search = 0 } = {} } = orgData?.ok
    ? orgData.data
    : {};

  // Helpers
  const canAddQuery =
    max_num_queries_per_search === 0 ||
    queries.length < max_num_queries_per_search;

  // Local state
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
  const [newTags, setNewTags] = useState<string[]>([]);
  const [isNearDistanceSelectOpen, setIsNearDistanceSelectOpen] =
    useState<boolean>(false);
  const [isNotNearDistanceSelectOpen, setIsNotNearDistanceSelectOpen] =
    useState<boolean>(false);

  const nearNearKeywords = useWatch({
    control: form.control,
    name: `queries.${queryIndex}.nearNearKeywords`,
  });

  return (
    <Card data-testid="query-row">
      <CardHeader>
        <CardTitle>
          <Trans>Söktermer</Trans>
        </CardTitle>
      </CardHeader>

      <CardContent>
        <FieldGroup>
          <div className="grid grid-cols-2 gap-x-4 gap-y-5">
            <Controller
              name={`queries.${queryIndex}.keywords`}
              control={form.control}
              render={({ field, fieldState }) => {
                const id = `form-qb-queries-${queryIndex}-keywords`;

                return (
                  <Field
                    data-invalid={fieldState.invalid}
                    className="col-span-2"
                  >
                    <FieldLabel htmlFor={id}>
                      <Trans>Sökord</Trans>
                    </FieldLabel>
                    <BadgeInput
                      value={field.value || []}
                      onChange={(value) => {
                        form.setValue(`queries.${queryIndex}.keywords`, value, {
                          shouldValidate: true,
                        });
                      }}
                      inputProps={{
                        placeholder: field.value?.length
                          ? undefined
                          : t`Ange det varumärke, den organisation eller det ämne du vill bevaka. Tryck Enter efter varje sökord.`,
                        id,
                        "aria-invalid": fieldState.invalid,
                        autoComplete: "off",
                        className: "keywords",
                      }}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name={`queries.${queryIndex}.nearKeywords`}
              control={form.control}
              render={({ field, fieldState }) => {
                const id = `form-qb-queries-${queryIndex}-near-keywords`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Sambandsord</Trans>
                    </FieldLabel>
                    <InputGroup className="h-auto">
                      <BadgeInput
                        variant="cyan"
                        withBorder={false}
                        value={field.value || []}
                        onChange={(newValue) => {
                          form.setValue(
                            `queries.${queryIndex}.nearKeywords`,
                            newValue
                          );
                        }}
                        inputProps={{
                          placeholder: field.value?.length
                            ? undefined
                            : t`Ange ord som måste förekomma i samband med dina sökord`,
                          id,
                          "aria-invalid": fieldState.invalid,
                          autoComplete: "off",
                        }}
                      />

                      <Controller
                        name={`queries.${queryIndex}.nearDistance`}
                        control={form.control}
                        render={({
                          field: distanceField,
                          fieldState: distanceFieldState,
                        }) => {
                          const distanceId = `form-qb-queries-${queryIndex}-near-distance`;
                          return (
                            <Popover>
                              <InputGroupAddon align="inline-end">
                                <PopoverTrigger asChild>
                                  <InputGroupButton
                                    aria-invalid={distanceFieldState.invalid}
                                    aria-label={t`Inställningar`}
                                    title={t`Inställningar`}
                                    size="icon-xs"
                                  >
                                    <Settings2Icon />
                                  </InputGroupButton>
                                </PopoverTrigger>
                              </InputGroupAddon>
                              <PopoverContent
                                className="w-96 flex flex-col gap-4 items-start"
                                onInteractOutside={(e) => {
                                  // Prevent closing if Select dropdown is open
                                  if (isNearDistanceSelectOpen) {
                                    e.preventDefault();
                                  }
                                }}
                              >
                                <Field
                                  data-invalid={distanceFieldState.invalid}
                                >
                                  <FieldLabel htmlFor={distanceId}>
                                    <Trans>Avstånd</Trans>
                                  </FieldLabel>

                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">
                                      <Trans>Högst</Trans>
                                    </span>
                                    <Select
                                      value={
                                        distanceField.value?.toString() || ""
                                      }
                                      open={isNearDistanceSelectOpen}
                                      onOpenChange={setIsNearDistanceSelectOpen}
                                      onValueChange={(value) => {
                                        form.setValue(
                                          `queries.${queryIndex}.nearDistance`,
                                          parseInt(value, 10)
                                        );
                                      }}
                                    >
                                      <SelectTrigger
                                        id={distanceId}
                                        aria-invalid={
                                          distanceFieldState.invalid
                                        }
                                      >
                                        <SelectValue
                                          placeholder={t`Välj avstånd`}
                                        />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {DISTANCE_VALUES.map((value) => (
                                          <SelectItem
                                            key={value}
                                            value={value.toString()}
                                          >
                                            {value}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <span className="text-sm text-muted-foreground truncate">
                                      {plural(distanceField.value || 0, {
                                        one: "ord bort från sökorden",
                                        other: "ord bort från sökorden",
                                      })}
                                    </span>
                                  </div>

                                  {distanceFieldState.invalid && (
                                    <FieldError
                                      errors={[distanceFieldState.error]}
                                    />
                                  )}
                                </Field>

                                {queries[queryIndex]
                                  ?.nearNearKeywords ? null : (
                                  <Button
                                    variant="secondary"
                                    type="button"
                                    size="sm"
                                    data-testid="form-qb-queries-near-near-keywords-add-button"
                                    onClick={() => {
                                      form.setValue(
                                        `queries.${queryIndex}.nearNearKeywords`,
                                        []
                                      );
                                    }}
                                  >
                                    <PlusIcon />
                                    <Trans>I närheten av</Trans>
                                  </Button>
                                )}
                              </PopoverContent>
                            </Popover>
                          );
                        }}
                      />
                    </InputGroup>

                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            {nearNearKeywords ? (
              <Controller
                name={`queries.${queryIndex}.nearNearKeywords`}
                control={form.control}
                render={({ field, fieldState }) => {
                  const id = `form-qb-queries-${queryIndex}-near-near-keywords`;

                  return (
                    <Field
                      data-invalid={fieldState.invalid}
                      className="self-end"
                    >
                      <FieldLabel htmlFor={id} className="sr-only">
                        <Trans>Ord att inkludera</Trans>
                      </FieldLabel>
                      <InputGroup className="h-auto">
                        <BadgeInput
                          variant="cyan"
                          withBorder={false}
                          data-testid={id}
                          value={field.value || []}
                          onChange={(newValue) => {
                            form.setValue(
                              `queries.${queryIndex}.nearNearKeywords`,
                              newValue
                            );
                          }}
                          inputProps={{
                            placeholder: field.value?.length
                              ? undefined
                              : t`Ange ord som måste förekomma i samband med dina sambandsord`,
                            id,
                            "aria-invalid": fieldState.invalid,
                            autoComplete: "off",
                          }}
                        />

                        <InputGroupAddon align="inline-end">
                          <InputGroupButton
                            aria-label={t`Ta bort`}
                            title={t`Ta bort`}
                            size="icon-xs"
                            type="button"
                            onClick={() => {
                              form.setValue(
                                `queries.${queryIndex}.nearNearKeywords`,
                                undefined
                              );
                            }}
                          >
                            <Trash2Icon />
                          </InputGroupButton>
                        </InputGroupAddon>
                      </InputGroup>

                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  );
                }}
              />
            ) : null}

            <Controller
              name={`queries.${queryIndex}.notNearKeywords`}
              control={form.control}
              render={({ field, fieldState }) => {
                const id = `form-qb-queries-${queryIndex}-not-near-keywords`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Exkludera ord</Trans>
                    </FieldLabel>
                    <InputGroup className="h-auto">
                      <BadgeInput
                        variant="red"
                        withBorder={false}
                        value={field.value || []}
                        onChange={(newValue) => {
                          form.setValue(
                            `queries.${queryIndex}.notNearKeywords`,
                            newValue
                          );
                        }}
                        inputProps={{
                          placeholder: field.value?.length
                            ? undefined
                            : t`Ange ord du vill utesluta från resultaten`,
                          id,
                          "aria-invalid": fieldState.invalid,
                          autoComplete: "off",
                        }}
                      />

                      <Controller
                        name={`queries.${queryIndex}.notNearDistance`}
                        control={form.control}
                        render={({
                          field: distanceField,
                          fieldState: distanceFieldState,
                        }) => {
                          const distanceId = `form-qb-queries-${queryIndex}-not-near-distance`;
                          return (
                            <Popover>
                              <InputGroupAddon align="inline-end">
                                <PopoverTrigger asChild>
                                  <InputGroupButton
                                    aria-invalid={distanceFieldState.invalid}
                                    aria-label={t`Inställningar`}
                                    title={t`Inställningar`}
                                    size="icon-xs"
                                  >
                                    <Settings2Icon />
                                  </InputGroupButton>
                                </PopoverTrigger>
                              </InputGroupAddon>
                              <PopoverContent
                                className="w-96"
                                onInteractOutside={(e) => {
                                  // Prevent closing if Select dropdown is open
                                  if (isNotNearDistanceSelectOpen) {
                                    e.preventDefault();
                                  }
                                }}
                              >
                                <Field
                                  data-invalid={distanceFieldState.invalid}
                                >
                                  <FieldLabel htmlFor={distanceId}>
                                    <Trans>Avstånd</Trans>
                                  </FieldLabel>

                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">
                                      <Trans>Högst</Trans>
                                    </span>
                                    <Select
                                      value={
                                        distanceField.value?.toString() || ""
                                      }
                                      open={isNotNearDistanceSelectOpen}
                                      onOpenChange={
                                        setIsNotNearDistanceSelectOpen
                                      }
                                      onValueChange={(value) => {
                                        form.setValue(
                                          `queries.${queryIndex}.notNearDistance`,
                                          parseInt(value, 10)
                                        );
                                      }}
                                    >
                                      <SelectTrigger
                                        id={distanceId}
                                        aria-invalid={
                                          distanceFieldState.invalid
                                        }
                                      >
                                        <SelectValue
                                          placeholder={t`Välj avstånd`}
                                        />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {DISTANCE_VALUES.map((value) => (
                                          <SelectItem
                                            key={value}
                                            value={value.toString()}
                                          >
                                            {value}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <span className="text-sm text-muted-foreground truncate">
                                      {plural(distanceField.value || 0, {
                                        one: "ord bort från sökorden",
                                        other: "ord bort från sökorden",
                                      })}
                                    </span>
                                  </div>

                                  {distanceFieldState.invalid && (
                                    <FieldError
                                      errors={[distanceFieldState.error]}
                                    />
                                  )}
                                </Field>
                              </PopoverContent>
                            </Popover>
                          );
                        }}
                      />
                    </InputGroup>

                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name={`queries.${queryIndex}.languages`}
              control={form.control}
              render={({ field, fieldState }) => {
                const id = `form-qb-queries-${queryIndex}-languages`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Språk</Trans>
                    </FieldLabel>
                    <DropdownMenu>
                      <DropdownMenuTrigger id={id} asChild>
                        <Button
                          variant="outline"
                          className="text-left justify-between font-normal"
                          aria-invalid={fieldState.invalid}
                        >
                          {field.value?.length === languages.length ? (
                            <Trans>Alla</Trans>
                          ) : (
                            <span className="truncate">
                              {field.value
                                ?.map((language) =>
                                  i18n._(LANGUAGE_CODE_TO_LABEL[language])
                                )
                                .join(", ")}
                            </span>
                          )}

                          <ChevronDownIcon className="text-muted-foreground opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width)">
                        {field.value?.length ? (
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();

                              form.setValue(
                                `queries.${queryIndex}.languages`,
                                []
                              );
                            }}
                          >
                            <Trans>Avmarkera alla</Trans>
                          </DropdownMenuItem>
                        ) : null}

                        {field.value?.length !== languages.length ? (
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();

                              form.setValue(
                                `queries.${queryIndex}.languages`,
                                languages
                              );
                            }}
                          >
                            <Trans>Markera alla</Trans>
                          </DropdownMenuItem>
                        ) : null}

                        <DropdownMenuSeparator />

                        {languages.map((language) => {
                          const isChecked = field.value?.includes(language);

                          return (
                            <DropdownMenuCheckboxItem
                              key={language}
                              checked={isChecked}
                              onSelect={(e) => {
                                e.preventDefault();
                              }}
                              onCheckedChange={(newIsChecked) => {
                                let newLanguages: Language[] =
                                  field.value || [];

                                if (newIsChecked) {
                                  newLanguages = [...newLanguages, language];
                                } else {
                                  newLanguages = newLanguages.filter(
                                    (l) => l !== language
                                  );
                                }

                                form.setValue(
                                  `queries.${queryIndex}.languages`,
                                  newLanguages
                                );
                              }}
                            >
                              {i18n._(LANGUAGE_CODE_TO_LABEL[language])}
                            </DropdownMenuCheckboxItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name={`queries.${queryIndex}.sources`}
              control={form.control}
              render={({ field, fieldState }) => {
                const id = `form-qb-queries-${queryIndex}-sources`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Medietyp</Trans>
                    </FieldLabel>
                    <DropdownMenu>
                      <DropdownMenuTrigger id={id} asChild>
                        <Button
                          variant="outline"
                          className="text-left justify-between font-normal"
                          aria-invalid={fieldState.invalid}
                        >
                          {field.value?.length === sources.length ? (
                            <Trans>Alla</Trans>
                          ) : (
                            <span className="truncate">
                              {field.value
                                ?.map((source) =>
                                  i18n._(SOURCE_CODE_TO_LABEL[source])
                                )
                                .join(", ")}
                            </span>
                          )}

                          <ChevronDownIcon className="text-muted-foreground opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width)">
                        {field.value?.length ? (
                          <DropdownMenuItem
                            id={`form-qb-queries-${queryIndex}-sources-uncheck-all`}
                            onSelect={(e) => {
                              e.preventDefault();

                              form.setValue(
                                `queries.${queryIndex}.sources`,
                                []
                              );
                            }}
                          >
                            <Trans>Avmarkera alla</Trans>
                          </DropdownMenuItem>
                        ) : null}

                        {field.value?.length !== sources.length ? (
                          <DropdownMenuItem
                            id={`form-qb-queries-${queryIndex}-sources-check-all`}
                            onSelect={(e) => {
                              e.preventDefault();

                              form.setValue(
                                `queries.${queryIndex}.sources`,
                                sources
                              );
                            }}
                          >
                            <Trans>Markera alla</Trans>
                          </DropdownMenuItem>
                        ) : null}

                        {socialSources.length ? (
                          <>
                            <DropdownMenuSeparator />

                            <DropdownMenuLabel>
                              <Trans>Sociala medier</Trans>
                            </DropdownMenuLabel>

                            {socialSources.map((source) => {
                              const isChecked = field.value?.includes(source);

                              return (
                                <DropdownMenuCheckboxItem
                                  id={`form-qb-queries-${queryIndex}-sources-${source}`}
                                  key={source}
                                  checked={isChecked}
                                  onSelect={(e) => {
                                    e.preventDefault();
                                  }}
                                  onCheckedChange={(newIsChecked) => {
                                    let newSources: Source[] =
                                      field.value || [];

                                    if (newIsChecked) {
                                      newSources = [...newSources, source];
                                    } else {
                                      newSources = newSources.filter(
                                        (s) => s !== source
                                      );
                                    }

                                    form.setValue(
                                      `queries.${queryIndex}.sources`,
                                      newSources
                                    );
                                  }}
                                >
                                  {i18n._(SOURCE_CODE_TO_LABEL[source])}
                                </DropdownMenuCheckboxItem>
                              );
                            })}
                          </>
                        ) : null}

                        {audioSources.length ? (
                          <>
                            <DropdownMenuSeparator />

                            <DropdownMenuLabel>
                              <Trans>Ljud</Trans>
                            </DropdownMenuLabel>

                            {audioSources.map((source) => {
                              const isChecked = field.value?.includes(source);

                              return (
                                <DropdownMenuCheckboxItem
                                  id={`form-qb-queries-${queryIndex}-sources-${source}`}
                                  key={source}
                                  checked={isChecked}
                                  onSelect={(e) => {
                                    e.preventDefault();
                                  }}
                                  onCheckedChange={(newIsChecked) => {
                                    let newSources: Source[] =
                                      field.value || [];

                                    if (newIsChecked) {
                                      newSources = [...newSources, source];
                                    } else {
                                      newSources = newSources.filter(
                                        (s) => s !== source
                                      );
                                    }

                                    form.setValue(
                                      `queries.${queryIndex}.sources`,
                                      newSources
                                    );
                                  }}
                                >
                                  {i18n._(SOURCE_CODE_TO_LABEL[source])}
                                </DropdownMenuCheckboxItem>
                              );
                            })}
                          </>
                        ) : null}

                        {newsSources.length ? (
                          <>
                            <DropdownMenuSeparator />

                            <DropdownMenuLabel>
                              <Trans>Nyheter</Trans>
                            </DropdownMenuLabel>

                            {newsSources.map((source) => {
                              const isChecked = field.value?.includes(source);

                              return (
                                <DropdownMenuCheckboxItem
                                  id={`form-qb-queries-${queryIndex}-sources-${source}`}
                                  key={source}
                                  checked={isChecked}
                                  onSelect={(e) => {
                                    e.preventDefault();
                                  }}
                                  onCheckedChange={(newIsChecked) => {
                                    let newSources: Source[] =
                                      field.value || [];

                                    if (newIsChecked) {
                                      newSources = [...newSources, source];
                                    } else {
                                      newSources = newSources.filter(
                                        (s) => s !== source
                                      );
                                    }

                                    form.setValue(
                                      `queries.${queryIndex}.sources`,
                                      newSources
                                    );
                                  }}
                                >
                                  {i18n._(SOURCE_CODE_TO_LABEL[source])}
                                </DropdownMenuCheckboxItem>
                              );
                            })}
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Collapsible
              open={isAdvancedOpen}
              className={cn(
                "col-span-2 px-4 bg-muted-secondary rounded-lg",

                // Error state.
                "has-[[data-slot][aria-invalid=true]]:ring-[3px] has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40"
              )}
              onOpenChange={setIsAdvancedOpen}
            >
              <CollapsibleTrigger
                id={`form-qb-queries-${queryIndex}-advanced`}
                className="flex items-center justify-between w-full py-4"
              >
                <p className="text-sm text-muted-foreground">
                  <Trans>Avancerade inställningar</Trans>
                </p>

                {isAdvancedOpen ? (
                  <MinusIcon className="size-4 text-muted-foreground" />
                ) : (
                  <PlusIcon className="size-4 text-muted-foreground" />
                )}
              </CollapsibleTrigger>

              <CollapsibleContent
                forceMount
                className={cn("pb-4", isAdvancedOpen ? "visible" : "hidden")}
              >
                <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                  <Controller
                    name={`queries.${queryIndex}.includeChannelNames`}
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = `form-qb-queries-${queryIndex}-include-channel-names`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Endast i källor</Trans>
                          </FieldLabel>
                          <BadgeInput
                            variant="cyan"
                            withBackground
                            value={
                              (field.value?.filter(Boolean) as string[]) || []
                            }
                            onChange={(value) => {
                              form.setValue(
                                `queries.${queryIndex}.includeChannelNames`,
                                value
                              );
                            }}
                            inputProps={{
                              "aria-invalid": fieldState.invalid,
                              placeholder: field.value?.filter(Boolean)?.length
                                ? undefined
                                : t`Lägg till källor att begränsa resultatet till`,
                              autoComplete: "off",
                              id,
                            }}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    name={`queries.${queryIndex}.excludeChannelNames`}
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = `form-qb-queries-${queryIndex}-exclude-channel-names`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Exkludera källor</Trans>
                          </FieldLabel>
                          <BadgeInput
                            variant="red"
                            withBackground
                            value={
                              (field.value?.filter(Boolean) as string[]) || []
                            }
                            onChange={(value) => {
                              form.setValue(
                                `queries.${queryIndex}.excludeChannelNames`,
                                value
                              );
                            }}
                            inputProps={{
                              "aria-invalid": fieldState.invalid,
                              placeholder: field.value?.filter(Boolean)?.length
                                ? undefined
                                : t`Lägg till källor att exkludera`,
                              autoComplete: "off",
                              id,
                            }}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    name={`queries.${queryIndex}.excludeTopics`}
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = `form-qb-queries-${queryIndex}-exclude-topics`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Exkludera ämnen</Trans>
                          </FieldLabel>
                          <BadgeInput
                            variant="red"
                            withBackground
                            data-testid={id}
                            value={
                              (field.value?.filter(Boolean) as string[]) || []
                            }
                            onChange={(value) => {
                              form.setValue(
                                `queries.${queryIndex}.excludeTopics`,
                                value
                              );
                            }}
                            inputProps={{
                              placeholder: field.value?.filter(Boolean)?.length
                                ? undefined
                                : t`Lägg till ämnen att exkludera`,
                              id,
                              "aria-invalid": fieldState.invalid,
                              autoComplete: "off",
                            }}
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    name={`queries.${queryIndex}.mentionTypes`}
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = `form-qb-queries-${queryIndex}-mention-types`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Välj typ av omnämnanden</Trans>
                          </FieldLabel>
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              form.setValue(
                                `queries.${queryIndex}.mentionTypes`,
                                value as MentionSourceType
                              );
                            }}
                          >
                            <SelectTrigger
                              id={id}
                              aria-invalid={fieldState.invalid}
                            >
                              <SelectValue placeholder={i18n._(t`Välj`)}>
                                {field.value ? (
                                  field.value === MENTION_SOURCE_TYPE.ALL ? (
                                    <Trans>Ljud & Text (förvalt)</Trans>
                                  ) : (
                                    i18n._(
                                      MENTION_SOURCE_TYPE_TO_LABEL[field.value]
                                    )
                                  )
                                ) : null}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {MENTION_SOURCE_TYPES.map((mentionSourceType) => (
                                <SelectItem
                                  key={mentionSourceType}
                                  value={mentionSourceType}
                                >
                                  {mentionSourceType ===
                                  MENTION_SOURCE_TYPE.ALL ? (
                                    <Trans>Ljud & Text (förvalt)</Trans>
                                  ) : (
                                    i18n._(
                                      MENTION_SOURCE_TYPE_TO_LABEL[
                                        mentionSourceType
                                      ]
                                    )
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  {sources.includes(SOURCE.NEWS) && (
                    <Controller
                      name={`queries.${queryIndex}.includePaywalled`}
                      control={form.control}
                      render={({ field, fieldState }) => {
                        const id = `form-qb-queries-${queryIndex}-include-paywalled`;

                        return (
                          <Field data-invalid={fieldState.invalid}>
                            <FieldLabel htmlFor={id}>
                              <Trans>
                                Inkludera ofullständiga betalväggsträffar
                              </Trans>
                            </FieldLabel>

                            <Switch
                              id={id}
                              checked={field.value}
                              onCheckedChange={(newValue) => {
                                form.setValue(
                                  `queries.${queryIndex}.includePaywalled`,
                                  newValue
                                );
                              }}
                            />

                            {fieldState.invalid && (
                              <FieldError errors={[fieldState.error]} />
                            )}
                          </Field>
                        );
                      }}
                    />
                  )}

                  <Controller
                    name={`queries.${queryIndex}.tags`}
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const id = `form-qb-queries-${queryIndex}-tags`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={id}>
                            <Trans>Tagga omnämnanden automatiskt</Trans>
                          </FieldLabel>

                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                id={id}
                                variant="outline"
                                className="text-left justify-between font-normal"
                                aria-invalid={fieldState.invalid}
                              >
                                {field.value?.length ? (
                                  field.value.join(", ")
                                ) : (
                                  <Trans>Lägg till taggar</Trans>
                                )}

                                <ChevronDownIcon className="text-muted-foreground opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              forceMount
                              className="w-(--radix-popover-trigger-width) p-0"
                            >
                              <TagSelector
                                queryIndex={queryIndex}
                                value={field.value || []}
                                newTags={newTags}
                                tags={tags}
                                onChange={(newTags) => {
                                  form.setValue(
                                    `queries.${queryIndex}.tags`,
                                    newTags
                                  );
                                }}
                                onAddNewTag={(newTag) => {
                                  setNewTags((prevNewTags) => [
                                    ...prevNewTags,
                                    newTag,
                                  ]);
                                }}
                              />
                            </PopoverContent>
                          </Popover>

                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />

                  {minReachByChannelTypeSources.length ? (
                    <Controller
                      name={`queries.${queryIndex}.minReachByChannelType`}
                      control={form.control}
                      render={({ field, fieldState }) => {
                        const id = `form-qb-queries-${queryIndex}-min-reach-by-channel-type`;

                        return (
                          <Field data-invalid={fieldState.invalid}>
                            <FieldLabel htmlFor={id}>
                              <Trans>Minsta räckvidd</Trans>
                            </FieldLabel>

                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  id={id}
                                  variant="outline"
                                  className="text-left justify-between font-normal"
                                  aria-invalid={fieldState.invalid}
                                >
                                  <Trans>Hantera minsta räckvidd</Trans>

                                  <ChevronDownIcon className="text-muted-foreground opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-(--radix-popover-trigger-width) max-h-96 overflow-y-auto overscroll-y-contain flex flex-col gap-4">
                                <div className="flex flex-col gap-1.5">
                                  <h4 className="font-medium">
                                    <Trans>Minsta räckvidd</Trans>
                                  </h4>
                                  <p className="text-sm text-muted-foreground text-balance">
                                    <Trans>
                                      Lägsta räckvidd för träffar. Noll betyder
                                      att alla träffar visas, oavsett räckvidd.
                                    </Trans>
                                  </p>
                                </div>

                                <Field>
                                  <FieldLabel
                                    htmlFor={`form-qb-queries-${queryIndex}-min-reach-by-channel-type-use-standard-values`}
                                  >
                                    <Trans>Använd standardvärden</Trans>
                                  </FieldLabel>
                                  <Switch
                                    id={`form-qb-queries-${queryIndex}-min-reach-by-channel-type-use-standard-values`}
                                    checked={
                                      field.value &&
                                      "has_custom_values" in field.value
                                        ? !field.value.has_custom_values
                                        : false
                                    }
                                    onCheckedChange={(newValue) => {
                                      const currentValue =
                                        field.value as MinReachByChannelTypeValues;

                                      form.setValue(
                                        `queries.${queryIndex}.minReachByChannelType`,
                                        newValue
                                          ? getMinReachByChannelTypeDefaultValues(
                                              minReachByChannelTypeSources
                                            )
                                          : {
                                              ...currentValue,
                                              has_custom_values: true,
                                            }
                                      );
                                    }}
                                  />
                                </Field>

                                {field.value &&
                                "has_custom_values" in field.value ? (
                                  <div className="flex flex-col gap-2">
                                    {minReachByChannelTypeSources.map(
                                      (source) => {
                                        const currentValue =
                                          field.value as MinReachByChannelTypeValues;

                                        const value = currentValue[source];

                                        const id = `form-qb-queries-${queryIndex}-min-reach-${source}`;

                                        return (
                                          <Field
                                            orientation="horizontal"
                                            key={source}
                                          >
                                            <FieldLabel
                                              className="min-w-20"
                                              htmlFor={id}
                                            >
                                              {i18n._(
                                                SOURCE_CODE_TO_LABEL[source]
                                              )}
                                            </FieldLabel>
                                            <Input
                                              id={id}
                                              type="text"
                                              value={
                                                typeof value === "number" &&
                                                !Number.isNaN(value)
                                                  ? value.toLocaleString()
                                                  : ""
                                              }
                                              disabled={
                                                !currentValue.has_custom_values
                                              }
                                              onChange={(e) => {
                                                const unformattedValue =
                                                  e.currentTarget.value.replaceAll(
                                                    /\D/g,
                                                    ""
                                                  );

                                                form.setValue(
                                                  `queries.${queryIndex}.minReachByChannelType`,
                                                  {
                                                    ...currentValue,
                                                    [source]:
                                                      parseInt(
                                                        unformattedValue,
                                                        10
                                                      ) || 0,
                                                  }
                                                );
                                              }}
                                            />
                                          </Field>
                                        );
                                      }
                                    )}
                                  </div>
                                ) : null}

                                <PopoverClose asChild>
                                  <Button variant="secondary">
                                    <Trans>Klar</Trans>
                                  </Button>
                                </PopoverClose>
                              </PopoverContent>
                            </Popover>

                            {fieldState.invalid && (
                              <FieldError errors={[fieldState.error]} />
                            )}
                          </Field>
                        );
                      }}
                    />
                  ) : null}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </FieldGroup>
      </CardContent>

      <CardFooter>
        {queries.length > 1 ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              queriesField.remove(queryIndex);
            }}
          >
            <Trash2Icon />
            <Trans>Ta bort söktermer</Trans>
          </Button>
        ) : null}

        {canAddQuery ? (
          <Button
            type="button"
            variant="outline"
            data-testid="form-qb-add-query-button"
            onClick={() => {
              queriesField.append(defaultQuery);
            }}
          >
            <PlusIcon />
            <Trans>Lägg till ytterligare söktermer</Trans>
          </Button>
        ) : null}

        <AccessControl level={ACCESS_LEVELS.STAFF}>
          {canAddQuery ? (
            <Button
              type="button"
              variant="outline"
              data-testid="form-qb-duplicate-query-button"
              onClick={() => {
                const upToDateQuery = form.getValues(`queries.${queryIndex}`);
                // Copy settings but not keywords
                queriesField.append({
                  ...upToDateQuery,
                  uuid: undefined,
                  keywords: [],
                  nearKeywords: [],
                  notNearKeywords: [],
                  nearNearKeywords: undefined,
                });
              }}
            >
              <CopyIcon />
              <Trans>Duplicera söktermer</Trans>
            </Button>
          ) : null}
        </AccessControl>
      </CardFooter>
    </Card>
  );
}
